/**
 * Performance tier colors aligned with common LoL stat sites (notably OP.GG match history):
 * KDA <3 grey · 3–3.99 green · 4–4.99 blue · 5+ orange
 */
export type StatTier = "excellent" | "good" | "average" | "low" | "poor" | "neutral";

const STAT_TIER_CLASSES: Record<StatTier, string> = {
  excellent: "font-jetbrains tabular-nums font-bold text-[#ff9e2c]",
  good: "font-jetbrains tabular-nums font-semibold text-[#5383e8]",
  average: "font-jetbrains tabular-nums font-semibold text-[#11a860]",
  low: "font-jetbrains tabular-nums text-[#9e9eb1]",
  poor: "font-jetbrains tabular-nums font-semibold text-[#e84057]",
  neutral: "font-jetbrains tabular-nums text-[#75818a]",
};

export const statTierClass = (tier: StatTier): string => STAT_TIER_CLASSES[tier];

export const computeKda = (
  kills?: number,
  deaths?: number,
  assists?: number,
  kda?: number,
): number | undefined => {
  if (kda != null) return kda;
  if (kills == null && deaths == null && assists == null) return undefined;
  const k = kills ?? 0;
  const d = deaths ?? 0;
  const a = assists ?? 0;
  if (k + a === 0 && d === 0) return 0;
  return (k + a) / Math.max(d, 1);
};

/** OP.GG-style KDA buckets: 5+ orange, 4+ blue, 3+ green, 2+ grey, <2 red */
export const kdaTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value >= 5) return "excellent";
  if (value >= 4) return "good";
  if (value >= 3) return "average";
  if (value >= 2) return "low";
  return "poor";
};

/** Solo-queue carry CS/min benchmarks (approx. OP.GG / u.gg lane averages at plat+) */
export const csPerMinTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value >= 8.5) return "excellent";
  if (value >= 7.5) return "good";
  if (value >= 6.5) return "average";
  if (value >= 5.5) return "low";
  return "poor";
};

/** Teamfight damage per minute — high impact thresholds for pro/FPL pace */
export const dpmTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value >= 700) return "excellent";
  if (value >= 600) return "good";
  if (value >= 500) return "average";
  if (value >= 400) return "low";
  return "poor";
};

/** Kill participation % — strong roams/teamfight presence benchmarks */
export const kpPctTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value >= 75) return "excellent";
  if (value >= 65) return "good";
  if (value >= 55) return "average";
  if (value >= 45) return "low";
  return "poor";
};

/** Average kills per game */
export const killsPerGameTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value >= 5) return "excellent";
  if (value >= 4) return "good";
  if (value >= 3) return "average";
  if (value >= 2) return "low";
  return "poor";
};

/** Average deaths per game — lower is better */
export const deathsPerGameTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value <= 2) return "excellent";
  if (value <= 2.75) return "good";
  if (value <= 3.5) return "average";
  if (value <= 4.5) return "low";
  return "poor";
};

/** Average assists per game */
export const assistsPerGameTier = (value: number | undefined): StatTier => {
  if (value == null) return "neutral";
  if (value >= 8) return "excellent";
  if (value >= 6) return "good";
  if (value >= 4.5) return "average";
  if (value >= 3) return "low";
  return "poor";
};
